import { actions, actionTypes, selectors } from 'data'
import Cookies from 'js-cookie'
import includes from 'lodash/includes'
import { all, call, delay, fork, put, select, takeEvery } from 'redux-saga/effects'
import * as countriesApi from 'services/api/country'
import { getVeriffMetadata } from 'services/api/kyc'
import URL from 'url-parse'
import { Response } from 'utils/fetchApi'

import { languagePrefixes } from '../../providers/ConnectedIntl/languages'
import {
  CountryType,
  NotificationDuration,
  NotificationTitleType,
  NotificationType,
  UsStateType,
  VeriffMetadataType
} from './types'
import zendeskSagas from './Zendesk/sagas'

const notificationManager = function* () {
  while (true) {
    yield delay(1000)

    const notifications: Array<NotificationType> = yield select(selectors.app.getNotifications)

    if (notifications.length) {
      yield put(actions.app.removeExpiredNotif())
    }
  }
}

export const notifyError = function* (
  error: string | void,
  title: NotificationTitleType,
  subtext?: string
) {
  yield put(
    actions.app.addNotif({
      created: +new Date(),
      description: error || undefined,
      duration: 5,
      subtext,
      title,
      type: 'danger'
    })
  )
}

export const notifySuccess = function* (
  title: NotificationTitleType,
  duration: void | NotificationDuration,
  description?: string,
  descValue?: string,
  subtext?: string
) {
  yield put(
    actions.app.addNotif({
      created: +new Date(),
      descValue,
      description,
      duration: duration || 5,
      subtext,
      title,
      type: 'success'
    })
  )
}

export const notifyWarning = function* (
  title: NotificationTitleType,
  duration: void | NotificationDuration
) {
  yield put(
    actions.app.addNotif({
      created: +new Date(),
      duration: duration || 2,
      title,
      type: 'warning'
    })
  )
}

const getCountries = function* () {
  const stateCountries: Array<CountryType> = yield select(selectors.app.getCountries)

  if (stateCountries.length > 0) {
    return
  }

  const [countries, states]: [Response<Array<CountryType>>, Response<Array<UsStateType>>] =
    yield all([call(countriesApi.getAll), call(countriesApi.getUsStates)])

  if (!countries.ok || !states.ok) {
    if (!countries.ok && countries.error.cause) {
      yield call(notifyError, countries.error.cause, 'KO_GENERIC')
    }

    if (!states.ok && states.error.cause) {
      yield call(notifyError, states.error.cause, 'KO_GENERIC')
    }
  } else {
    yield put(actions.app.setCountries(countries.response))
    yield put(actions.app.setUsStates(states.response))
  }
}

const getVeriffMetadataSaga = function* () {
  const data: Response<VeriffMetadataType> = yield call(getVeriffMetadata)

  if (!data.ok) {
    yield put(actions.app.setVeriffMetadataError(data.error))
    return
  }

  yield put(actions.app.setVeriffMetadata(data.response))
}

const initLanguage = function* () {
  try {
    const { pathname } = window.location
    const url = new URL(pathname)
    const languagePrefix = url.pathname.split('/')[1]
    const languageCookie = Cookies.get('clang')
    if (includes(languagePrefixes, languagePrefix)) {
      yield put(actions.app.setLanguage(languagePrefix))
    } else if (includes(languagePrefixes, languageCookie)) {
      yield put(actions.app.setLanguage(languageCookie))
    } else {
      yield put(actions.app.setLanguage('en'))
    }
  } catch (e) {}
}

const displayLoginMessaging = function* () {
  const config = yield select(selectors.config.getConfig)

  const { newMarginPairs } = config
  if (newMarginPairs.length > 0) {
    let seenMarginPairs: string[] = []
    try {
      seenMarginPairs = Cookies.get('newMarginPairs').split(',')
    } catch (e) {}

    const firstUnseenMarginPair = newMarginPairs.find((c) => seenMarginPairs.indexOf(c) === -1)
    if (firstUnseenMarginPair) {
      yield put(actions.app.showModal('NEW_MARGIN_PAIR', { pair: firstUnseenMarginPair }))
      Cookies.set('newMarginPairs', [...seenMarginPairs, firstUnseenMarginPair].join(','), {
        expires: 365
      })
      return
    }
  }

  const { newCurrencies } = config
  if (!newCurrencies.length) {
    return
  }

  let seenCurrencies: string[] = []
  try {
    seenCurrencies = Cookies.get('whatsNewModal').split(',')
  } catch (e) {}

  const firstUnseen = newCurrencies.find((c) => seenCurrencies.indexOf(c) === -1)
  if (!firstUnseen) {
    // seen all
    return
  }

  Cookies.set('whatsNewModal', [...seenCurrencies, firstUnseen].join(','), { expires: 365 })
}

export default function* appMain() {
  yield takeEvery(actionTypes.app.GET_COUNTRIES, getCountries)
  yield takeEvery(actionTypes.app.GET_LOGIN_MESSAGING, displayLoginMessaging)
  yield takeEvery(actionTypes.app.GET_VERIFF_METADATA, getVeriffMetadataSaga)
  yield fork(notificationManager)
  yield fork(initLanguage)
  yield fork(zendeskSagas)
}
